import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Copy, RefreshCw, Trash2, Plus, KeyRound, UserPlus } from "lucide-react";

type Guest = {
  role_id: string;
  user_id: string;
  role: "guest" | "member";
  email: string | null;
  label: string | null;
  expires_at: string | null;
  created_at: string;
  expired: boolean;
};

const PRESETS = [
  { label: "Mobile United", email: "guest+mobileunited@emrkt.co" },
  { label: "Todd — Innovation Portal", email: "guest+innovationportal@emrkt.co" },
  { label: "Japonica Brown — Mobile Chamber", email: "guest+mobilechamber@emrkt.co" },
];

export default function GuestsAdminTab({ password }: { password: string }) {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [days, setDays] = useState(30);
  const [role, setRole] = useState<"guest" | "member">("member");
  const [promoteEmail, setPromoteEmail] = useState("");
  const [autoGrant, setAutoGrant] = useState(false);
  const [lastCreated, setLastCreated] = useState<
    { email: string; password: string; label?: string | null } | null
  >(null);

  const call = async (action: string, extra: Record<string, unknown> = {}) => {
    const { data, error } = await supabase.functions.invoke("manage-guests", {
      body: { action, ...extra },
      headers: { "x-admin-password": password },
    });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const load = async () => {
    try {
      const data = await call("list");
      setGuests(data.guests || []);
      try {
        const s = await call("get_settings");
        setAutoGrant(!!s?.auto_grant_member_on_signup);
      } catch {
        // settings endpoint may not be deployed yet
      }
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e?: { email: string; label: string }) => {
    const payload = e ?? { email, label };
    if (!payload.email) {
      toast.error("Email required");
      return;
    }
    setLoading(true);
    try {
      const data = await call("create", {
        email: payload.email,
        label: payload.label || null,
        days,
        role,
      });
      setLastCreated({
        email: data.guest.email,
        password: data.guest.password,
        label: data.guest.label,
      });
      toast.success(
        `${role === "member" ? "Member" : "Guest"} created — copy the password now`,
      );
      setEmail("");
      setLabel("");
      await load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const promote = async () => {
    if (!promoteEmail) {
      toast.error("Email required");
      return;
    }
    setLoading(true);
    try {
      await call("promote", { email: promoteEmail, role });
      toast.success(`Promoted ${promoteEmail} to ${role}`);
      setPromoteEmail("");
      await load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleAutoGrant = async (next: boolean) => {
    try {
      await call("set_auto_grant_member", { enabled: next });
      setAutoGrant(next);
      toast.success(
        next
          ? "New signups will now auto-receive member access"
          : "New signups will land on the waitlist",
      );
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const provisionAll = async () => {
    setLoading(true);
    try {
      const results: { email: string; password: string; label: string }[] = [];
      for (const p of PRESETS) {
        const exists = guests.find((g) => g.email === p.email);
        if (exists) continue;
        const data = await call("create", {
          email: p.email,
          label: p.label,
          days,
          role: "guest",
        });
        results.push({
          email: data.guest.email,
          password: data.guest.password,
          label: data.guest.label,
        });
      }
      await load();
      if (results.length === 0) {
        toast.info("All preset guests already exist");
      } else {
        const block = results
          .map((r) => `${r.label}\n${r.email}\n${r.password}`)
          .join("\n\n");
        await navigator.clipboard.writeText(block).catch(() => {});
        toast.success(
          `Created ${results.length} guest${results.length === 1 ? "" : "s"} — credentials copied to clipboard`,
        );
        // Surface the last one in the in-page banner as well
        setLastCreated(results[results.length - 1]);
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const extend = async (g: Guest) => {
    try {
      await call("extend", { role_id: g.role_id, days: 30 });
      toast.success("Extended 30 days");
      await load();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const resetPwd = async (g: Guest) => {
    try {
      const data = await call("reset_password", { user_id: g.user_id });
      setLastCreated({ email: g.email ?? "", password: data.password, label: g.label });
      toast.success("Password reset — copy it now");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const revoke = async (g: Guest) => {
    if (!confirm(`Revoke access for ${g.email}? This deletes the account.`)) return;
    try {
      await call("revoke", { role_id: g.role_id, user_id: g.user_id, delete_user: true });
      toast.success("Revoked");
      await load();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const copy = (s: string) => {
    navigator.clipboard.writeText(s).catch(() => {});
    toast.success("Copied");
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-5 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold">Signup access</h2>
          <p className="text-sm text-muted-foreground mt-1">
            When on, every new signup is immediately granted permanent <strong>member</strong> access (no waitlist).
            Keep this off during private beta.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs text-muted-foreground">
            {autoGrant ? "Open signups" : "Waitlist on"}
          </span>
          <Switch checked={autoGrant} onCheckedChange={toggleAutoGrant} />
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="font-display text-lg font-semibold">Access accounts</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Create a brand-new account (guest = time-limited, member = permanent),
              or promote a user who already signed up and is on the waitlist.
            </p>
          </div>
          <Button onClick={provisionAll} disabled={loading} variant="outline">
            Provision 3 guest presets
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Email</Label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@email.com"
              className="mt-1 bg-background"
            />
          </div>
          <div>
            <Label className="text-xs">Label</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Name / org"
              className="mt-1 bg-background"
            />
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as "guest" | "member")}>
              <SelectTrigger className="mt-1 bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member (permanent)</SelectItem>
                <SelectItem value="guest">Guest (time-limited)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Days {role === "member" && "(guest only)"}</Label>
            <Input
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              disabled={role !== "guest"}
              className="mt-1 bg-background"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3 mt-4">
          <Button
            onClick={() => create()}
            disabled={loading}
            className="bg-primary text-primary-foreground"
          >
            <Plus className="h-4 w-4 mr-2" /> Create new account
          </Button>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-xs">Promote existing signup</Label>
              <Input
                value={promoteEmail}
                onChange={(e) => setPromoteEmail(e.target.value)}
                placeholder="waitlisted@email.com"
                className="mt-1 bg-background w-64"
              />
            </div>
            <Button onClick={promote} disabled={loading} variant="outline">
              <UserPlus className="h-4 w-4 mr-2" /> Grant {role}
            </Button>
          </div>
        </div>
      </div>

      {lastCreated && (
        <div className="border border-primary/40 bg-primary/5 rounded-xl p-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-sm font-semibold text-primary">
              Credentials — visible only once
            </p>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setLastCreated(null)}
            >
              Dismiss
            </Button>
          </div>
          <div className="text-sm font-mono space-y-1">
            {lastCreated.label && <div className="text-muted-foreground">{lastCreated.label}</div>}
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">email:</span>
              <span>{lastCreated.email}</span>
              <Button size="sm" variant="ghost" onClick={() => copy(lastCreated.email)}>
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">password:</span>
              <span>{lastCreated.password}</span>
              <Button size="sm" variant="ghost" onClick={() => copy(lastCreated.password)}>
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {guests.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No guest accounts yet.
          </p>
        ) : (
          guests.map((g) => (
            <div
              key={g.role_id}
              className="bg-card border border-border rounded-xl p-4 flex items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold truncate">
                    {g.label || g.email}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      g.role === "member"
                        ? "bg-secondary text-secondary-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {g.role}
                  </span>
                  {g.expired ? (
                    <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">
                      Expired
                    </span>
                  ) : (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      Active
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                  {g.email} • expires{" "}
                  {g.expires_at
                    ? new Date(g.expires_at).toLocaleDateString()
                    : "never"}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                {g.role === "guest" && (
                  <Button size="sm" variant="ghost" onClick={() => extend(g)} title="Extend 30 days">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => resetPwd(g)} title="Reset password">
                  <KeyRound className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => revoke(g)}
                  title="Revoke"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}